/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { observable, reaction } from 'mobx';
import { INosOriginOfInterestPickerData } from './nos-origin-of-interest-picker.data';
import { INosOriginOfInterestPickerProps } from './nos-origin-of-interest-picker.props.autogenerated';
import classnames from 'classnames';
import { IGetCustomerSourcesData } from '../../actions/get-customer-sources.action';
import BusinessSignUpDropdown from './components/sign-up-dropdown';
import { NOS_BUSINESS_SIGN_UP_REFERRAL_SET_EVENT, NOS_BUSINESS_SIGN_UP_REFERRAL_SOURCE_KEY } from '../../configuration/business-sign-up';

export interface INosOriginOfInterestExtendedProps extends INosOriginOfInterestPickerProps<INosOriginOfInterestPickerData> {
    updateDropdown(newSelectValue: IGetCustomerSourcesData): void;
    selectedValue: IGetCustomerSourcesData;
    isSubmittedPressed: boolean;
}

export interface INosOriginOfInterestPickerViewProps extends INosOriginOfInterestPickerProps<INosOriginOfInterestPickerData> {
    className: string;
    CustomerSourcePicker: IModuleProps;
    customerSources: any;
}

/**
 *
 * NosOriginOfInterestPicker component
 * @extends {React.PureComponent<INosOriginOfInterestPickerProps<INosOriginOfInterestPickerData>>}
 */
class NosOriginOfInterestPicker extends React.PureComponent<INosOriginOfInterestExtendedProps> {
    @observable private customerSources?: IGetCustomerSourcesData[];
    private _referralSource: string | null = null;

    public constructor(props: INosOriginOfInterestExtendedProps) {
        super(props);

        const { data } = this.props;

        this.customerSources = data.customerSources.result || [];
    }

    private _onReferralSet = (ev: Event) => {
        const detail = (ev as CustomEvent<string>).detail;
        this._referralSource = detail;
        this._dataInitialize(this.props);
    };

    public componentDidMount(): void {
        this._referralSource = sessionStorage.getItem(NOS_BUSINESS_SIGN_UP_REFERRAL_SOURCE_KEY);
        this._onReferralSet = (ev: Event) => {
            const detail = (ev as CustomEvent).detail as string;
            this._referralSource = detail;
            this._dataInitialize(this.props);
        };
        window.addEventListener(NOS_BUSINESS_SIGN_UP_REFERRAL_SET_EVENT, this._onReferralSet);

        this._dataInitialize(this.props);
    }

    public componentDidUpdate(prevProps: INosOriginOfInterestExtendedProps): void {
        const sources = prevProps.data?.customerSources?.result ?? [];

        // Only proceed when the list has gone from empty (or different) to populated
        if (sources.length) {
            if (this._referralSource) {
                const referral = this._referralSource;

                const matched = sources.find((el: IGetCustomerSourcesData) => {
                    const candidates = [el.key, el.value, el.CustomerSourceReferralCode];
                    return candidates.some(candidate => candidate?.toLocaleLowerCase() === referral.toLocaleLowerCase());
                });

                if (matched) {
                    const normalized = {
                        key: matched.key ?? matched.key,
                        value: matched.value ?? matched.value,
                        referralCode: matched.CustomerSourceReferralCode,
                        ExtensionProperties: []
                    };
                    this.props.updateDropdown(normalized);
                }

                // clear so it runs only once
                this._referralSource = null;
            }
        }
    }

    private readonly _dataInitialize = (props: INosOriginOfInterestExtendedProps): void => {
        const { data } = props;

        reaction(
            () => data.customerSources.result,
            () => {
                this.customerSources = data.customerSources.result ?? [];
            }
        );
    };

    private readonly _onInputChange = (event: React.ChangeEvent<HTMLSelectElement>, customerSources: IGetCustomerSourcesData[]) => {
        const newChosenCustomerSource = customerSources.find(el => el.key === event.target.value);
        if (newChosenCustomerSource) {
            this.props.updateDropdown(newChosenCustomerSource);
        }
    };

    private readonly _initializeDropdown = (customerSources: IGetCustomerSourcesData[], selectedValue: IGetCustomerSourcesData) => {
        const tempOptions = customerSources?.map(el => {
            return {
                key: el.key,
                value: el.value,
                referralCode: el.CustomerSourceReferralCode
            };
        });
        return (
            <BusinessSignUpDropdown
                className={``}
                isRequired={false}
                isSubmittedPressed={this.props.isSubmittedPressed}
                options={tempOptions}
                onChange={(event: any) => this._onInputChange(event, customerSources)}
                selectValue={selectedValue}
                value={selectedValue?.value ?? ''}
                resources={this.props.resources}
            />
        );
    };

    public render(): JSX.Element | null {
        const { renderView, config, selectedValue } = this.props;

        const viewProps = {
            ...this.props,
            className: config.className,
            CustomerSourcePicker: {
                moduleProps: this.props,
                className: classnames('ms-business-account-address', config.className)
            },
            customerSources: this._initializeDropdown(this.customerSources ?? [], selectedValue)
        };

        return renderView(viewProps);
    }
}

export default NosOriginOfInterestPicker;
